/**
 * SearchPayments slice — generic, filter-driven payment search.
 *
 * searchPayments(args): POST /payments/search via beaconApi; stores the raw
 * SearchPayments summaries + total count. Callers supply the SearchPaymentsInput
 * (filters/pagination/sort). The artifact detail page uses it with
 * filters.paymentIdentifiers = [artifactId]; other callers can reuse it with
 * any filter.
 *
 * A global search keeps each region's page separate in `regionResults`;
 * `payments` is every region's rows flattened, for single-region consumers.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ApiPaymentSummary, SearchPaymentsInput } from './searchPaymentsApi';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';
import { resolveApiBase } from '../env/envState';
import {
  RegionSearchState, SearchFanOutResult, describeFailures, fanOutSearch,
  flattenPages, regionBases, regionSearchInitialState,
} from '../regionFanOut';

/**
 * `region` pins the search to one region, for callers that already know which
 * region holds the parent entity — the artifact and execution pages looking up
 * the payments they carry. Omitted, it follows the selection and fans out
 * across every region in a global search.
 */
export interface SearchPaymentsArgs {
  input: SearchPaymentsInput;
  region?: string;
}

export interface SearchPaymentsState extends RegionSearchState<ApiPaymentSummary> {
  /** All regions' rows in one list, for the single-region consumers. */
  payments: ApiPaymentSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  currentRequestId: string | null;
  /** Last search input; re-dispatched on region change. */
  lastInput: SearchPaymentsInput | null;
}

export const searchPaymentsInitialState: SearchPaymentsState = {
  payments: [],
  total: 0,
  loading: false,
  error: null,
  currentRequestId: null,
  lastInput: null,
  ...regionSearchInitialState,
};

export const searchPayments = createAsyncThunk<
  SearchFanOutResult<ApiPaymentSummary>, SearchPaymentsArgs, { state: RootState; rejectValue: string }
>('searchPayments/run', async ({ input, region }, { getState }) => {
  const env = getState().env;
  const bases = region
    ? [{ region, apiBase: resolveApiBase(env.stage, region) }]
    : regionBases(env);
  const run = async (apiBase: string) => {
    const response = await beaconApi.searchPayments(input, apiBase);
    return { items: response.payments ?? [], total: response.totalPaymentsCount ?? 0 };
  };
  return fanOutSearch(bases, run);
});

const searchPaymentsSlice = createSlice({
  name: 'searchPayments',
  initialState: searchPaymentsInitialState,
  reducers: {
    clearSearchPayments: () => searchPaymentsInitialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchPayments.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
        state.lastInput = action.meta.arg.input;
      })
      .addCase(searchPayments.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        const { regionResults, searchedRegions, errors } = action.payload;
        const flat = flattenPages(regionResults);
        state.loading = false;
        state.regionResults = regionResults;
        state.searchedRegions = searchedRegions;
        state.regionErrors = errors;
        // Flattened view for the single-region consumers (the artifact and
        // execution pages' payments table and analytics).
        state.payments = flat.items;
        state.total = flat.total;
        state.error = flat.items.length === 0 && errors.length > 0
          ? `No region returned payments. ${describeFailures(errors)}`
          : null;
      })
      .addCase(searchPayments.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to search payments';
      });
  },
});

export const { clearSearchPayments } = searchPaymentsSlice.actions;
export default searchPaymentsSlice.reducer;
